import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import { jwtDecode } from 'jwt-decode';
import i18n from 'i18next';
import { useForm } from 'react-hook-form';
import decodeToken from '../../../../utils/decode-token';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import SettingsPage from './settings-page';
import SettingsSection from './settings-section';
import PasswordSettingsSection from './password-settings-section';
import useAvailableLanguages from './use-available-languages';
import LanguageSettingsSection from './language-settings-section';

function PmaSettingsContent() {
  const { t } = useTranslation();

  const token = sessionStorage.getItem('token') || null;
  const decodedToken = jwtDecode(token);
  const userId = decodedToken.id;
  const role = decodeToken(token).role;

  const [areaCode, setAreaCode] = useState(''); // paÃ­s base del usuario (para cargar idiomas)
  const [assignedAreaCodes, setAssignedAreaCodes] = useState([]);
  const [userLanguage, setUserLanguage] = useState('');
  const { languages, error: languagesError } = useAvailableLanguages({
    areaCodes: assignedAreaCodes.length > 0 ? assignedAreaCodes : [areaCode].filter(Boolean),
  });

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [areaPhones, setAreaPhones] = useState({});

  const [pmaEmail, setPmaEmail] = useState('');
  const [pmaRole, setPmaRole] = useState('');
  const [isNominator, setIsNominator] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const normalizeAreaCode = (code) => (code === 'LUX' ? 'LU' : code);
  const toPhoneIso2 = (code) => normalizeAreaCode(code || '').toLowerCase();
  const isEmptyPhone = (v) => !v || v.replace(/\D/g, '').length === 0;

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    getValues,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          console.error('No se encontrÃ³ ningÃºn token en sessionStorage.');
          return;
        }

        const url = `${process.env.REACT_APP_API_URL}/pma?pmaId=${userId}`;
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const response = await apiClient.get(url, config);

        const {
          name,
          last_name,
          prefix_phone,
          phone: phoneOnly,
          preferred_language,
          area_code,
          area_code_visibility = [],
          email,
          role: profileRole,
          is_nominator,
        } = response.data;

        setValue('name', name || '');
        setValue('lastName', last_name || '');

        setPmaEmail(email || '');
        setPmaRole(profileRole || '');
        setIsNominator(Boolean(is_nominator));

        const fullPhone = `${prefix_phone || ''}${phoneOnly || ''}`;
        setPhone(fullPhone);

        setUserLanguage(preferred_language || '');
        const visibleAreaCodes = (area_code_visibility || [])
          .map((item) => item.area_code)
          .filter(Boolean);
        const availableAreaCodes = [...new Set([area_code, ...visibleAreaCodes].filter(Boolean))];
        setAreaCode(area_code || visibleAreaCodes[0] || '');
        setAssignedAreaCodes(availableAreaCodes);

        const initialAreaPhones = {};
        (area_code_visibility || []).forEach((v) => {
          const code = v.area_code;
          initialAreaPhones[code] = {
            value: `${v.prefix_phone || ''}${v.phone || ''}`,
            error: '',
            touched: false,
          };
        });
        setAreaPhones(initialAreaPhones);
      } catch (error) {
        if (error.response) {
          setSnackbarOpen(true);
          setSnackbarSeverity('error');
          setSnackbarMessage(t('common-internal-server-error'));
        }
      }
    };

    fetchData();
  }, [setValue, userId, t, token]);

  useEffect(() => {
    if (!languagesError) return;
    setSnackbarOpen(true);
    setSnackbarSeverity('error');
    setSnackbarMessage(t('common-internal-server-error'));
  }, [languagesError, t]);

  const validatePhone = (inputPhone) => {
    const phoneNumber = parsePhoneNumberFromString(inputPhone);
    if (!phoneNumber || !phoneNumber.isValid()) {
      setPhoneError(t('alert-phone-format'));
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    validatePhone(value);
  };

  const buildAreaCodeVisibility = (generalPrefix, generalNumber) => {
    const areaCodes = Object.keys(areaPhones);
    return areaCodes.map((code) => {
      const state = areaPhones[code] || { value: '' };

      let prefix_phone = '';
      let phoneNumber = '';

      if (!isEmptyPhone(state.value)) {
        const parsed = parsePhoneNumberFromString(state.value);
        if (parsed && parsed.isValid() && parsed.nationalNumber) {
          prefix_phone = `+${parsed.countryCallingCode}`;
          phoneNumber = parsed.nationalNumber.toString();
        }
      }

      if (!prefix_phone || !phoneNumber) {
        prefix_phone = generalPrefix;
        phoneNumber = generalNumber;
      }

      return { area_code: code, prefix_phone, phone: phoneNumber };
    });
  };

  const putPma = async ({
                          name,
                          last_name,
                          preferred_language,
                          prefix_phone,
                          phone: phoneNumber,
                          area_code_visibility,
                        }) => {
    const url = `${process.env.REACT_APP_API_URL}/pma/${userId}`;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    const payload = {
      email: pmaEmail,
      is_nominator: isNominator,
      area_code: areaCode,
      name,
      last_name,
      preferred_language,
      prefix_phone,
      phone: phoneNumber,
      area_code_visibility,
    };

    return apiClient.put(url, payload, config);
  };

  const saveContactInfo = async (data) => {
    try {
      const parsedPhone = parsePhoneNumberFromString(phone);
      if (!parsedPhone || !parsedPhone.isValid()) {
        setSnackbarMessage(t('alert-phone-format'));
        setSnackbarSeverity('error');
        setFocus('phone');
        setSnackbarOpen(true);
        return;
      }

      const generalPrefix = `+${parsedPhone.countryCallingCode}`;
      const generalNumber = parsedPhone.nationalNumber?.toString() || '';
      const area_code_visibility = buildAreaCodeVisibility(generalPrefix, generalNumber);

      const response = await putPma({
        name: data.name,
        last_name: data.lastName,
        preferred_language: userLanguage,
        prefix_phone: generalPrefix,
        phone: generalNumber,
        area_code_visibility,
      });

      if (response.status === 200 || response.status === 201) {
        sessionStorage.removeItem('loggedUser');
        setSnackbarMessage(t('personal-settings-save-success'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      console.error('Error al realizar la solicitud:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarOpen(true);
    }
  };

  const saveLanguage = async (e) => {
    e.preventDefault();

    try {
      const { name, lastName } = getValues();

      const parsedPhone = parsePhoneNumberFromString(phone);
      if (!parsedPhone || !parsedPhone.isValid()) {
        setSnackbarMessage(t('alert-phone-format'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const generalPrefix = `+${parsedPhone.countryCallingCode}`;
      const generalNumber = parsedPhone.nationalNumber?.toString() || '';
      const area_code_visibility = buildAreaCodeVisibility(generalPrefix, generalNumber);

      const response = await putPma({
        name,
        last_name: lastName,
        preferred_language: userLanguage,
        prefix_phone: generalPrefix,
        phone: generalNumber,
        area_code_visibility,
      });

      if (response.status === 200 || response.status === 201) {
        i18n.changeLanguage(userLanguage);
        sessionStorage.removeItem('loggedUser');
        setSnackbarMessage(t('language-settings-save-success'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      console.error('Error al realizar la solicitud:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarOpen(true);
    }
  };

  return (
    <SettingsPage
      allowed={Boolean(
        token &&
        (role === 'pma' || role === 'pmaGM') &&
        (!pmaRole || pmaRole === role)
      )}
      backTo="/pma/main/"
      feedback={{ open: snackbarOpen, message: snackbarMessage, severity: snackbarSeverity }}
      onCloseFeedback={handleSnackbarClose}
    >
                <SettingsSection title={t('personal-settings-title')}>
                  <form onSubmit={handleSubmit(saveContactInfo)}>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <div className={`form-control required ${errors.name ? 'error' : ''}`}>
                          <label htmlFor="name">{t('common-first-name')}</label>
                          <input
                            maxLength="50"
                            type="text"
                            id="name"
                            name="name"
                            autoComplete="off"
                            {...register('name', { required: t('alert-name') })}
                          />
                          {errors.name && <span>{errors.name.message}</span>}
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <div className={`form-control required ${errors.lastName ? 'error' : ''}`}>
                          <label htmlFor="lastName">{t('common-last-name')}</label>
                          <input
                            maxLength="50"
                            type="text"
                            id="lastName"
                            name="lastName"
                            autoComplete="off"
                            {...register('lastName', { required: t('alert-last-name') })}
                          />
                          {errors.lastName && <span>{errors.lastName.message}</span>}
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <div className="form-control required">
                          <label htmlFor="phone">{t('common-phone')}</label>
                          <PhoneInput value={phone} onChange={handlePhoneChange} />
                          {phoneError && <span className="error-message">{phoneError}</span>}
                        </div>
                      </Grid>
                    </Grid>

                    {Object.keys(areaPhones).length > 0 && (
                      <Grid container columnSpacing={2} rowSpacing={1} sx={{ mt: 1 }}>
                        {Object.keys(areaPhones).map((code) => {
                          const state = areaPhones[code];
                          return (
                            <Grid key={code} size={{ xs: 12, sm: 6, md: 4 }}>
                              <div className="form-control">
                                <label>
                                  {t('common-phone')} â€” {t(`countries-${code}`)}
                                </label>
                                <PhoneInput
                                  defaultCountry={toPhoneIso2(code)}
                                  value={state.value}
                                  onChange={(val) => {
                                    setAreaPhones((prev) => {
                                      const next = { ...prev, [code]: { ...prev[code], value: val } };

                                      if (next[code].touched && !isEmptyPhone(val)) {
                                        const parsed = parsePhoneNumberFromString(val);
                                        next[code].error =
                                          !parsed || !parsed.isValid() || !parsed.nationalNumber
                                            ? t('alert-phone-format')
                                            : '';
                                      }

                                      if (next[code].touched && isEmptyPhone(val)) {
                                        next[code].error = '';
                                      }

                                      return next;
                                    });
                                  }}
                                  onBlur={() => {
                                    setAreaPhones((prev) => {
                                      const next = { ...prev, [code]: { ...prev[code], touched: true } };

                                      if (isEmptyPhone(next[code].value)) {
                                        next[code].error = '';
                                        return next;
                                      }

                                      const parsed = parsePhoneNumberFromString(next[code].value);
                                      next[code].error =
                                        !parsed || !parsed.isValid() || !parsed.nationalNumber
                                          ? t('alert-phone-format')
                                          : '';

                                      return next;
                                    });
                                  }}
                                />
                                {state.error && <span className="error-message">{state.error}</span>}
                              </div>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}

                    <div className="actions">
                      <button className="btn" type="submit">
                        {t('common-save-changes')}
                      </button>
                    </div>
                  </form>
                </SettingsSection>

                <LanguageSettingsSection
                  languages={languages}
                  selectedLanguage={userLanguage}
                  onLanguageChange={setUserLanguage}
                  onSave={saveLanguage}
                />
                <PasswordSettingsSection />
    </SettingsPage>
  );
}

export default PmaSettingsContent;
